import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PlusIcon, PencilIcon, ArchiveBoxArrowDownIcon, ArrowUturnLeftIcon } from "@heroicons/react/24/outline";

import { Campaign } from "@/common/model/Campaign";
import { CampaignService } from "@/services/CampaignService";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import Modal from "@/components/ui/Modal";

const STATUSES = ["draft", "active", "archived"];

const emptyForm: Partial<Campaign> = {
  name: "",
  description: "",
  status: "draft",
  fallbackUrl: "https://www.thesurfr.app",
  moosendListId: "",
};

function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<Partial<Campaign>>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCampaigns(await CampaignService.list());
    } catch (e: any) {
      toast.error(`Failed to load campaigns: ${e?.message ?? e}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setModalOpen(true);
  };
  const openEdit = (c: Campaign) => {
    setForm(c);
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.name?.trim() || !form.fallbackUrl?.trim()) {
      toast.error("Name and fallback URL are required");
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await CampaignService.update(form.id, form);
        toast.success("Campaign updated");
      } else {
        await CampaignService.create(form);
        toast.success("Campaign created");
      }
      setModalOpen(false);
      load();
    } catch (e: any) {
      toast.error(`Save failed: ${e?.response?.data?.error ?? e?.message ?? e}`);
    } finally {
      setSaving(false);
    }
  };

  // Soft delete: archive/restore via status. No hard deletes — recipient feedback is kept.
  const setArchived = async (c: Campaign, archived: boolean) => {
    if (archived && !window.confirm(`Archive campaign "${c.name}"? It's hidden from active use but all data is kept. You can restore it anytime.`)) return;
    try {
      await CampaignService.update(c.id, { status: archived ? "archived" : "active" });
      toast.success(archived ? "Campaign archived" : "Campaign restored");
      load();
    } catch (e: any) {
      toast.error(`${archived ? "Archive" : "Restore"} failed: ${e?.response?.data?.error ?? e?.message ?? e}`);
    }
  };

  const pct = (c: Campaign) =>
    c.recipients && c.recipients > 0 ? Math.round((100 * (c.answered ?? 0)) / c.recipients) : 0;

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Win-back Campaigns</h1>
          <p className="text-sm text-muted-foreground">
            Pre-generate tokenized feedback links, manage cancel reasons, and view answers.
          </p>
        </div>
        <Button onClick={openCreate}>
          <PlusIcon className="mr-1 h-4 w-4" /> New campaign
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-inputboxborder">
        <table className="w-full text-sm">
          <thead className="bg-backgroundbox text-left">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Recipients</th>
              <th className="px-3 py-2 text-right">Answered</th>
              <th className="px-3 py-2 text-right">Response</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && campaigns.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No campaigns yet.</td></tr>
            )}
            {campaigns.map((c) => (
              <tr key={c.id} className="border-t border-inputboxborder hover:bg-backgroundbox/50">
                <td className="px-3 py-2">
                  <button className="font-medium text-accent hover:underline" onClick={() => navigate(`/campaigns/${c.id}`)}>
                    {c.name}
                  </button>
                  {c.description && <div className="text-xs text-muted-foreground">{c.description}</div>}
                </td>
                <td className="px-3 py-2">
                  <span className="rounded-full bg-backgroundbox px-2 py-0.5 text-xs">{c.status}</span>
                </td>
                <td className="px-3 py-2 text-right">{c.recipients ?? 0}</td>
                <td className="px-3 py-2 text-right">{c.answered ?? 0}</td>
                <td className="px-3 py-2 text-right">{pct(c)}%</td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/campaigns/${c.id}`)}>Open</Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(c)}><PencilIcon className="h-4 w-4" /></Button>
                    {c.status === "archived" ? (
                      <Button size="sm" variant="outline" onClick={() => setArchived(c, false)} title="Restore"><ArrowUturnLeftIcon className="h-4 w-4" /></Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setArchived(c, true)} title="Archive"><ArchiveBoxArrowDownIcon className="h-4 w-4" /></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <div className="p-6 text-gray-900">
          <h2 className="mb-4 text-lg font-semibold">{form.id ? "Edit campaign" : "New campaign"}</h2>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Name</label>
              <Input value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Description</label>
              <Input value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Status</label>
              <select
                className="flex h-10 w-full rounded-md border border-inputboxborder bg-backgroundbox px-3 py-2 text-sm"
                value={form.status ?? "draft"}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Fallback URL</label>
              <Input value={form.fallbackUrl ?? ""} onChange={(e) => setForm({ ...form, fallbackUrl: e.target.value })} />
              <p className="mt-1 text-xs text-muted-foreground">Used when a link's token/code can't be resolved.</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Moosend list ID (optional)</label>
              <Input value={form.moosendListId ?? ""} onChange={(e) => setForm({ ...form, moosendListId: e.target.value })} placeholder="leave blank for the default list" />
              <p className="mt-1 text-xs text-muted-foreground">Recipients sync to this Moosend list; blank uses the global default.</p>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default CampaignsPage;
